<template>
  <div data-testid="add-materiality" class="all-height all-width">
    <div class="pa-6 d-flex flex-column gap-3">
      <!-- Page Header -->
      <div class="" data-testid="add-materiality__header">
        <Breadcrumbs :glCodeText="glCodeText" :glCodeInfo="glCodeInfo" :getStatusLabel="getStatusLabel"
          :isRefreshing="loading" :reportingType="reportingType" @back="handleBack" @refresh="handleRefresh"
          :showRefresh="true" :activeCommentary="{}" />
      </div>

      <!-- Loading State -->
      <div v-if="loading" data-testid="add-materiality__loading">
        <v-skeleton-loader type="article, table" />
      </div>

      <!-- Main Content -->
      <div v-else data-testid="add-materiality__content">

        <!-- Cycle Type Chip -->
        <div class="d-flex align-center mb-4" data-testid="add-materiality__cycle-chip"
          v-if="ruData.__type === 'single'">
          <h5 label color="commentaryMaterialityActual" v-if="cycle === 'Actual'" data-testid="add-materiality__cycle-label-actual">
            {{ $nova.labelsName('materiality_page', 'actual_label', 'Actual') }} Cycle
          </h5>
          <h5 label color="commentaryMaterialityForecast" v-else-if="cycle === 'Forecast'" data-testid="add-materiality__cycle-label-forecast">
            {{ $nova.labelsName('materiality_page', 'forecast_longform_label', 'Forecast') }} Cycle
          </h5>
        </div>

        <!-- RU Details Chips -->
        <div class="mb-2" data-testid="add-materiality__ru-details" v-if="ruData.__type === 'single'">
          <div class="d-flex gap-4">
            <v-chip class="chip-choose" data-testid="add-materiality__network-chip">
              <span class="categorystype">{{ $nova.labelsName('my_reporting_units_page', 'division_label', 'Network') }}
                - {{ ruData.network }}</span>
            </v-chip>
            <div class="vertical"></div>
            <v-chip class="chip-choose" data-testid="add-materiality__region-chip">
              <span class="categorystype">{{ $nova.labelsName('my_reporting_units_page', 'region_label', 'Region') }} -
                {{ ruData.region }}</span>
            </v-chip>
            <div class="vertical"></div>
            <v-chip class="chip-choose" data-testid="add-materiality__country-chip">
              <span class="categorystype">{{ $nova.labelsName('my_reporting_units_page', 'country_label', 'Country') }}
                - {{ ruData.country }}</span>
            </v-chip>
          </div>
        </div>
        <div class="mb-2" data-testid="add-materiality__ru-details-bulk" v-else>
          <CountCard data-testid="add-materiality__count-card" :count="ruData._id.length || 0"
            page-name="users available" label-key="total_users_label"
            :fallback-label="$nova.labelsName('materiality_page', 'ru_entity_plural_label', 'Reporting Units') + ' Selected'"
            :border-color="'#2C2C35'" :background-color="'#F7F7F7'" :height="'44px'" showWhenZero :loading="loading" />
        </div>

        <!-- Notes Section -->
        <div class="runots mb-6 mt-5" data-testid="add-materiality__notes">
          <div class="mb-1">
            <h5>Notes</h5>
            <ol class="noTransform pl-4 noteslist">
              <li v-for="(note, index) in materialityNotes" :key="index" class="noTransform"
                :data-testid="`add-materiality__note-item-${index + 1}`">
                {{ note }}
              </li>
            </ol>
          </div>
        </div>

        <!-- Tabs for RU/GL/SL Wise -->
        <v-card class="pa-4 d-flex flex-column gap-3" data-testid="add-materiality__tabs-card">
          <v-tabs v-model="selectedWiseTab" background-color="" class="wisetab" data-testid="add-materiality__tabs">
            <v-tab v-for="(tab, index) in wiseTabs" :key="index" :data-testid="`add-materiality__tab-${tab.value}`">
              {{ tab.label }}
            </v-tab>
          </v-tabs>

          <v-tabs-items v-model="selectedWiseTab" class="" data-testid="add-materiality__tab-content">
            <!-- RU Wise Tab -->
            <v-tab-item key="0" eager>
              <RUWiseMateriality data-testid="add-materiality__ru-wise" :ruData="ruData" :ruError="ruError"
                :ruLevelEdit="ruLevelEdit" :isViewMode="isViewMode" :isWPPClient="isWPPClient" :minDate="minDate"
                :maxDate="maxDate" :multipleMaterialityToggle="multipleMaterialityToggle"
                :formattedRUData="formattedRUData" :formattedRUErrorData="formattedRUErrorData"
                :infoMessage="infoMessage" :editableRUs="editableRUs"
                :viewRULevelMaterialityData="viewRULevelMaterialityData"
                :viewCycleType.sync="viewCycleType"
                :viewModuleType.sync="viewModuleType"
                @update:ruData="ruData = $event"
                @update:ruLevelEdit="ruLevelEdit = $event"
                @update:multipleMaterialityToggle="multipleMaterialityToggle = $event"
                @update:formattedRUData="formattedRUData = $event" @removeMonth="removeMonth" @editRu="handleEditRu"
                @cancelEditRu="handleCancelEditRu" @periods-changed="handlePeriodsChanged" />
            </v-tab-item>

            <!-- GL Wise Tab -->
            <v-tab-item key="1" eager>
              <GLWiseMateriality data-testid="add-materiality__gl-wise" :glLevelEdit="ruLevelEdit" :ruData="ruData"
                :glHeaders="glHeaders" :formattedGLData="formattedGLData" :glData="glData" :glError="glError"
                :editableGLs="editableGLs" :loading="loading" :minDate="minDate" :maxDate="maxDate"
                :multipleMaterialityToggle="multipleMaterialityToggle"
                :viewGLLevelMaterialityData="viewGLLevelMaterialityData"
                :viewCycleType.sync="viewCycleType"
                :viewModuleType.sync="viewModuleType"
                @update:ruData="ruData = $event"
                @editGL="handleEditGL" @cancelEditGL="handleCancelEditGL" @removeMonth="removeMonth"
                @periods-changed="handlePeriodsChanged" :isViewMode="isViewMode" />
            </v-tab-item>

            <!-- SL Wise Tab -->
            <v-tab-item key="2" eager>
              <SLWiseMateriality data-testid="add-materiality__sl-wise" :slLevelEdit="ruLevelEdit" :ruData="ruData"
                :glHeaders="glHeaders" :slFilterData="slFilterData" :glData="slData" :glError="slError"
                :editableGLs="editableSLs" :loading="loading" :minDate="minDate" :maxDate="maxDate"
                :multipleMaterialityToggle="multipleMaterialityToggle"
                :viewSLLevelMaterialityData="viewSLLevelMaterialityData"
                :viewCycleType.sync="viewCycleType"
                :viewModuleType.sync="viewModuleType"
                @update:ruData="ruData = $event"
                @editGL="handleEditSL" @cancelEditGL="handleCancelEditSL" @removeMonth="removeMonth"
                @periods-changed="handlePeriodsChanged" :isViewMode="isViewMode" />
            </v-tab-item>
          </v-tabs-items>


           <!-- Action Buttons -->
        <div class="d-flex align-center gap-3" v-if="!isViewMode" data-testid="add-materiality__actions">
          <!-- <span class="caption">*Required</span> -->
          <v-spacer></v-spacer>
          <v-btn color="secondaryBtn" @click="goBack" data-testid="add-materiality__cancel-btn">
            <span>Cancel</span>
          </v-btn>
          <v-btn color="primary" v-if="ruData.periods && ruData.periods.length > 0 && selectedWiseTab === 0"
            @click="updateRUThreshold" :disabled="hasNegativeRUValues" data-testid="add-materiality__update-ru-btn">
            <span>Update</span>
          </v-btn>
          <v-btn color="primary" v-else-if="ruData.periods && ruData.periods.length > 0 && selectedWiseTab === 1"
            @click="updateGLThreshold" :disabled="hasNegativeGLValues" data-testid="add-materiality__update-gl-btn">
            <span>Update</span>
          </v-btn>
          <v-btn color="primary" v-else-if="ruData.periods && ruData.periods.length > 0 && selectedWiseTab === 2"
            @click="updateSLThreshold" :disabled="hasNegativeSLValues" data-testid="add-materiality__update-sl-btn">
            <span>Update</span>
          </v-btn>
        </div>

        <!-- View Mode Back Button -->
        <div class="d-flex justify-end" v-else data-testid="add-materiality__view-actions">
          <v-btn color="primary" @click="goBack" data-testid="add-materiality__back-to-list-btn">
            <span>Back to List</span>
          </v-btn>
        </div>
        </v-card>

       
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import RUWiseMateriality from '../../components/Materiality/RUWiseMateriality.vue';
import GLWiseMateriality from '../../components/Materiality/GLWiseMateriality.vue';
import SLWiseMateriality from '../../components/Materiality/SLWiseMateriality.vue';
import Breadcrumbs from '../../components/Breadcrumbs.vue';
import CountCard from '@/components/CommonCountCard.vue';
import { MATERIALITY } from '../../constants';

/**
 * AddMateriality Page Component
 * Handles editing and viewing materiality thresholds as a separate page
 */
export default {
  name: 'AddMateriality',
  components: {
    RUWiseMateriality,
    GLWiseMateriality,
    SLWiseMateriality,
    Breadcrumbs,
    CountCard,
  },
  data() {
    return {
      // Loading state
      loading: false,

      // Tab configuration
      wiseTabs: [
        { label: 'RU', value: 'ruWise' },
        { label: 'GL', value: 'glWise' },
        { label: 'Drill Down ', value: 'slWise' },
      ],
      selectedWiseTab: 0,

      // Route parameters
      cycle: 'Actual',
      internalReportingType: 'pl', // Maps to cycle type internally
      editType: 'single',
      isViewMode: false,
      bulksubmit: false,
      selectedRUId: null,

      // View mode filter state (to preserve user selections)
      viewCycleType: 'actual', // 'actual' or 'forecast' - lowercase for MaterialityFiltersHeader
      viewModuleType: 'pl', // 'pl' or 'bs'

      // RU Data
      ruData: {},
      ruError: {},

      // GL Data
      glData: {},
      glError: {},
      formattedGLData: [],

      // SL Data (separate from GL)
      slData: {},
      slError: {},

      // RU Data
      formattedRUData: {},
      formattedRUErrorData: {},

      // Edit state
      ruLevelEdit: { local: true, regional: true, enable: true },
      editableGLs: { proforma: [], forecast: [] },
      editableSLs: { proforma: [], forecast: [] },
      editableRUs: { proforma: [], forecast: [] },
      multipleMaterialityToggle: false,
      infoMessage: 'Multiple materiality thresholds set at GL level',

      // Date constraints
      minDate: '',
      maxDate: '',

      // GL Headers
      glheaders: [
        {
          value: "code",
          text: this.$nova.labelsName('materiality_page', 'commentary_gl_code_label', 'GL Code') + ' & Name',
          sortable: true,
          filterable: true,
          width: "250px",
        },
        {
          value: "datatypes",
          text: "",
          sortable: false,
          filterable: false,
          lock: true,
        },
        {
          value: "edit_buttons",
          text: "Action",
          sortable: false,
          filterable: false,
          lock: true,
        },
        {
          value: "control_thresholds_amount",
          text: this.$nova.labelsName('materiality_page', 'commentary_local_control_amount_shortform_label', 'LC Amt'),
          alignment: "text-right",
          sortable: false,
          lock: true,
        },
        {
          value: "control_thresholds_percent",
          text: this.$nova.labelsName('materiality_page', 'commentary_local_control_percentage_shortform_label', 'LC %'),
          alignment: "text-right",
          sortable: false,
          lock: true,
        },
        {
          value: "management_thresholds_amount",
          text: this.$nova.labelsName('materiality_page', 'commentary_local_management_amount_shortform_label', 'LM Amt'),
          alignment: "text-right",
          sortable: false,
          lock: true,
        },
        {
          value: "management_thresholds_percent",
          text: this.$nova.labelsName('materiality_page', 'commentary_local_management_precentage_shortform_label', 'LM %'),
          alignment: "text-right",
          sortable: false,
          lock: true,
        },
        {
          value: "control_thresholds_amount_regional",
          text: this.$nova.labelsName('materiality_page', 'commentary_regional_control_amount_shortform_label', 'RC Amt'),
          alignment: "text-right",
          sortable: false,
          lock: true,
        },
        {
          value: "control_thresholds_percent_regional",
          text: this.$nova.labelsName('materiality_page', 'commentary_regional_control_percentage_shortform_label', 'RC %'),
          alignment: "text-right",
          sortable: false,
          lock: true,
        },
        {
          value: "management_thresholds_amount_regional",
          text: this.$nova.labelsName('materiality_page', 'commentary_regional_management_amount_shortform_label', 'RM Amt'),
          alignment: "text-right",
          sortable: false,
          lock: true,
        },
        {
          value: "management_thresholds_percent_regional",
          text: this.$nova.labelsName('materiality_page', 'commentary_regional_management_percentage_shortform_label', 'RM %'),
          alignment: "text-right",
          sortable: false,
          lock: true,
        },
      ],

      // Control threshold values (from parent config)
      control_thresholds_percent_proforma_value: MATERIALITY.DEFAULT_CONTROL_PERCENT,
      control_thresholds_percent_regional_proforma_value: MATERIALITY.DEFAULT_CONTROL_PERCENT,

      // GL List data from store
      glList: {},
      addeditGLData: {},
      addeditSLData: {},

      // View mode materiality level data (from Threshold.vue via Vuex store)
      viewRULevelMaterialityData: [],
      viewGLLevelMaterialityData: [],
      viewSLLevelMaterialityData: [],
    };
  },
  computed: {
    ...mapGetters('varianceanalysis/reportingUnit', [
      'getEditingMaterialityPayload',
      'getMaterialityGLList',
      'getMaterialityFormattedGLData',
      'getMaterialityAddeditGLData',
      'getMaterialityFormattedRUData',
      'getMaterialityFormattedRUErrorData',
      'getViewRULevelMaterialityData',
      'getViewGLLevelMaterialityData',
      'getViewSLLevelMaterialityData',
      'getMyMaterialityData',
      'getMyMaterialityAbsoluteThresholdValues',
      'getMyMaterialityLoading'
    ]),
    /**
     * GL Headers for the table
     */
    glHeaders() {
      return this.glheaders;
    },
    /**
     * Breadcrumb text for parent page
     */
    glCodeText() {
      return 'Materiality';
    },
    /**
     * Breadcrumb info for current page
     * Returns formatted string with proper null checks to prevent 'undefined - undefined'
     */
    glCodeInfo() {
      if (this.ruData.__type === 'bulk') {
        const cycleLabel = this.cycle === 'Actual'
          ? this.$nova.labelsName('materiality_page', 'actual_label', 'Actual')
          : this.$nova.labelsName('materiality_page', 'forecast_longform_label', 'Forecast');
        return `${cycleLabel}`;
      } else if (this.ruData.__type === 'single') {
        // Proper null checks to prevent 'undefined - undefined'
        const code = this.ruData.code || '';
        const name = this.ruData.name || '';
        if (code && name) {
          return `${code} - ${name}`;
        } else if (code) {
          return code;
        } else if (name) {
          return name;
        }
      }
      return 'Materiality Update';
    },
    /**
     * Conversation options (not used here)
     */
    breadcrumbsConversationOptions() {
      return null;
    },
    /**
     * Activity log options (not used here)
     */
    breadcrumbsActivityLogOptions() {
      return null;
    },
    /**
     * Reporting type
     */
    reportingType() {
      return 'pl';
    },
    /**
     * Check if current client is WPP client based on labels
     */
    isWPPClient() {
      return this.$nova.labelsName('materiality_page', 'unit_label', 'reporting unit') === 'reporting unit';
    },
    /**
     * Get materiality notes based on view/edit mode
     */
    materialityNotes() {
      const notes = [...MATERIALITY.NOTES.COMMON];
      if (this.isViewMode) {
        notes.push(...MATERIALITY.NOTES.VIEW_MODE);
      } else {
        notes.push(...MATERIALITY.NOTES.EDIT_MODE);
      }
      return notes;
    },
    /**
     * Filter GL data for SL tab (only items with sub_ledger_exists)
     */
    slFilterData() {
      return this.formattedGLData.filter((item) => item.sub_ledger_exists === true);
    },
    /**
     * Check if GL tab has unsaved changes
     */
    hasGLChanges() {
      return Object.keys(this.glData).length > 0;
    },
    /**
     * Check if SL tab has unsaved changes
     */
    hasSLChanges() {
      return Object.keys(this.slData).length > 0;
    },
    /**
     * Check if any RU materiality values are negative (both local and regional)
     * When multipleMaterialityToggle is true, also scans formattedRUData across all periods
     * @returns {boolean} True if any RU value is negative
     */
    hasNegativeRUValues() {
      const thresholdKeys = MATERIALITY.THRESHOLD_KEYS;

      // Check single-mode ruData fields
      const hasNegativeInRuData = thresholdKeys.some(field => {
        const value = Number(this.ruData[field]);
        return !isNaN(value) && value < 0;
      });
      if (hasNegativeInRuData) return true;

      // In multiple materiality mode, also check formattedRUData per period
      if (this.multipleMaterialityToggle && this.formattedRUData) {
        return Object.keys(this.formattedRUData).some(period => {
          const periodData = this.formattedRUData[period];
          if (!periodData) return false;
          return thresholdKeys.some(field => {
            const value = Number(periodData[field]);
            return !isNaN(value) && value < 0;
          });
        });
      }

      return false;
    },
    /**
     * Check if any GL materiality values are negative across all GL entries
     * @returns {boolean} True if any GL value is negative
     */
    hasNegativeGLValues() {
      const thresholdKeys = MATERIALITY.THRESHOLD_KEYS;
      return Object.keys(this.glData).some(id => {
        const entry = this.glData[id];
        if (!entry) return false;
        return thresholdKeys.some(field => {
          const value = Number(entry[field]);
          return !isNaN(value) && value < 0;
        });
      });
    },
    /**
     * Check if any SL materiality values are negative across all SL entries
     * @returns {boolean} True if any SL value is negative
     */
    hasNegativeSLValues() {
      const thresholdKeys = MATERIALITY.THRESHOLD_KEYS;
      return Object.keys(this.slData).some(id => {
        const entry = this.slData[id];
        if (!entry) return false;
        return thresholdKeys.some(field => {
          const value = Number(entry[field]);
          return !isNaN(value) && value < 0;
        });
      });
    },
  },
  /**
   * Initialize component with route parameters and load data
   */
  created() {
    this.initializeFromRoute();
    this.fetchGLList();
  },
  methods: {
    ...mapMutations('varianceanalysis', ['SET_MATERIALITY_VIEW_PERIODS']),
    ...mapActions('varianceanalysis/reportingUnit', [
      'fetchMyMateriality',
      'saveMaterialityThreshold'
    ]),
    /**
     * Map lowercase cycle type to capitalized format
     * @param {string} cycleType - Lowercase cycle type ('actual' or 'forecast')
     * @returns {string} Capitalized cycle type ('Actual' or 'Forecast')
     */
    mapCycleType(cycleType) {
      const cycleTypeMap = {
        'actual': 'Actual',
        'forecast': 'Forecast'
      };
      return cycleTypeMap[cycleType?.toLowerCase()] || cycleType;
    },
    handlePeriodsChanged(payload) {
      console.log('[AddMateriality]: Periods changed', payload);
      
      // Destructure the payload object
      const { periods, cycleType, reportingType } = payload || {};
      
      // Validate payload
      if (!periods || !Array.isArray(periods) || periods.length === 0) {
        console.warn('[AddMateriality]: Invalid periods in payload', payload);
        return;
      }
      
      // Preserve the user's selected view filter values
      // These will be passed back to MaterialityFiltersHeader to maintain state
      if (cycleType) {
        this.viewCycleType = cycleType; // Keep lowercase ('actual' or 'forecast')
      }
      if (reportingType) {
        this.viewModuleType = reportingType; // 'pl' or 'bs'
      }
      
      // Handle periods array - getRUData expects a single period string
      const selectedPeriod = periods[0]; // Use first period for view mode
      
      // Convert cycleType from lowercase to capitalized format
      const mappedCycleType = this.mapCycleType(cycleType);
      
      // Update cycle type if changed
      if (mappedCycleType && mappedCycleType !== this.cycle) {
        this.cycle = mappedCycleType;
      }
      
      // Update reporting type if changed
      // The watcher on ruData.reporting_type is guarded to skip edit-mode logic when isViewMode is true
      if (reportingType && reportingType !== this.internalReportingType) {
        this.internalReportingType = reportingType;
        this.ruData.reporting_type = reportingType;
      }
      
      // Fetch data with new filters
      this.getRUData({
        period: selectedPeriod,
        reporting_type: reportingType || this.ruData.reporting_type || this.internalReportingType,
        cycletype: mappedCycleType || this.cycle,
      });
    },
    /**
     * Format period from YYYY-MM to MM-YYYY format for display
     * @param {string|Array} period - Period in YYYY-MM format or array of periods
     * @returns {string|Array} Period in MM-YYYY format or array of formatted periods
     */
    formatPeriod(period) {
      if (!period) return '';

      // Handle array of periods
      if (Array.isArray(period)) {
        return period.map(p => {
          if (!p) return '';
          const [year, month] = p.split('-');
          return `${month}-${year}`;
        });
      }

      // Handle single period string
      const [year, month] = period.split('-');
      return `${month}-${year}`;
    },
    /**
     * Get status label (used by Breadcrumbs)
     */
    getStatusLabel(key, fallback) {
      return this.$nova.labelsName('materiality_page', key, fallback);
    },
    /**
     * Handle back navigation from Breadcrumbs
     */
    handleBack() {
      this.$router.back();
    },
    /**
     * Handle refresh from Breadcrumbs
     */
    handleRefresh() {
      // if (this.selectedRUId && !this.bulksubmit && this.isViewMode) {
      this.getRUData();
      // }
    },
    /**
     * Handle mark conversation read (no-op)
     */
    handleMarkConversationRead() {
      // No-op
    },
    /**
     * Load materiality data from Vuex store
     * Retrieves GL list, formatted GL data, and RU data set by Threshold.vue
     * If GL list is not available, fetches it from API
     */
    async loadDataFromStore() {
      // Load GL list from store
      const storedGLList = this.getMaterialityGLList;
      if (storedGLList && Object.keys(storedGLList).length > 0) {
        this.glList = storedGLList;
      } else {
        // Fetch GL list from API if not in store
        await this.fetchGLList();
      }

      // Load formatted GL data from store
      const storedFormattedGLData = this.getMaterialityFormattedGLData;
      if (storedFormattedGLData && storedFormattedGLData.length > 0) {
        this.formattedGLData = storedFormattedGLData;
      } else if (this.glList && Object.keys(this.glList).length > 0) {
        // Generate formatted GL data if we have glList but no formatted data
        this.formattedGLData = this.generateFormattedGLList({}, true);
      }

      // Load addedit GL data from store
      const storedAddeditGLData = this.getMaterialityAddeditGLData;
      if (storedAddeditGLData && Object.keys(storedAddeditGLData).length > 0) {
        this.addeditGLData = storedAddeditGLData;
        this.glData = storedAddeditGLData;
      }

      // Load formatted RU data from store
      const storedFormattedRUData = this.getMaterialityFormattedRUData;
      if (storedFormattedRUData && Object.keys(storedFormattedRUData).length > 0) {
        this.formattedRUData = storedFormattedRUData;
      } else if (this.multipleMaterialityToggle && this.ruData.periods && this.ruData.periods.length > 0) {
        // Generate formatted RU data if multiple materiality is enabled and periods are selected
        this.generateFormattedRUData();
      }

      // Load formatted RU error data from store
      const storedFormattedRUErrorData = this.getMaterialityFormattedRUErrorData;
      if (storedFormattedRUErrorData && Object.keys(storedFormattedRUErrorData).length > 0) {
        this.formattedRUErrorData = storedFormattedRUErrorData;
      }

      // Load view mode materiality level data from store (set by Threshold.vue)
      const storedViewRULevelData = this.getViewRULevelMaterialityData;
      if (storedViewRULevelData && (Array.isArray(storedViewRULevelData) ? storedViewRULevelData.length > 0 : Object.keys(storedViewRULevelData).length > 0)) {
        this.viewRULevelMaterialityData = storedViewRULevelData;
      }

      const storedViewGLLevelData = this.getViewGLLevelMaterialityData;
      if (storedViewGLLevelData && (Array.isArray(storedViewGLLevelData) ? storedViewGLLevelData.length > 0 : Object.keys(storedViewGLLevelData).length > 0)) {
        this.viewGLLevelMaterialityData = storedViewGLLevelData;
      }

      const storedViewSLLevelData = this.getViewSLLevelMaterialityData;
      if (storedViewSLLevelData && (Array.isArray(storedViewSLLevelData) ? storedViewSLLevelData.length > 0 : Object.keys(storedViewSLLevelData).length > 0)) {
        this.viewSLLevelMaterialityData = storedViewSLLevelData;
      }
    },
    /**
     * Generate formatted RU data for multiple materiality periods
     * Creates threshold data structure for each selected period
     */
    generateFormattedRUData() {
      if (!this.ruData.periods || !Array.isArray(this.ruData.periods)) {
        return;
      }
      console.log(this.ruData.periods,"this.ruData.periods");
      

      this.formattedRUData = {};
      this.formattedRUErrorData = {};

      // Create threshold data for each period
      this.ruData.periods.forEach(period => {
        // Convert period format from YYYY-MM to MM-YYYY for display
        const periodKey = period.split('-').reverse().join('-');

        this.formattedRUData[periodKey] = MATERIALITY.getDefaultRUThresholds();

        // Initialize error tracking for this period
        this.formattedRUErrorData[periodKey] = {
          control_thresholds_amount_proforma: false,
          control_thresholds_percent_proforma: false,
          management_thresholds_amount_proforma: false,
          management_thresholds_percent_proforma: false,
          control_thresholds_amount_regional_proforma: false,
          control_thresholds_percent_regional_proforma: false,
          management_thresholds_amount_regional_proforma: false,
          management_thresholds_percent_regional_proforma: false,
          control_thresholds_amount_forecast: false,
          control_thresholds_percent_forecast: false,
          management_thresholds_amount_forecast: false,
          management_thresholds_percent_forecast: false,
          control_thresholds_amount_regional_forecast: false,
          control_thresholds_percent_regional_forecast: false,
          management_thresholds_amount_regional_forecast: false,
          management_thresholds_percent_regional_forecast: false,
        };
      });

    },
    /**
     * Fetch GL list from Vuex store action
     * @param {string} reportingType - The reporting type to filter GL list ('pl' or 'bs')
     */
    async fetchGLList(reportingType = 'pl') {
      try {
        this.loading = true;
        // Use dynamic reportingType from parameter or ruData
        const filterType = reportingType || this.ruData?.reporting_type || 'pl';

        // Dispatch Vuex action to fetch GL list
        await this.$store.dispatch('varianceanalysis/reportingUnit/fetchGLList', {
          axiosInstance: this.axios,
          reportingType: filterType
        });

        // Get the fetched GL list from store
        this.glList = this.getMaterialityGLList;
        // Generate formatted GL data after fetching GL list
        this.formattedGLData = this.generateFormattedGLList({}, true);
         this.resetRuDataThresholds();
      } catch (err) {
        console.error('[AddMateriality]: Error fetching GL list', err);
      } finally {
        this.loading = false;
      }
    },
    /**
     * Reorder array by a key
     * @param {Array} dt - Data array
     * @param {string} key - Key to sort by
     * @returns {Array} - Sorted array
     */
    reorder(dt, key) {
      return dt.sort((a, b) => a[key] - b[key]);
    },
    /**
     * Initialize component state from route query parameters
     * Handles both Vuex store data and sessionStorage fallback for page reloads
     */
    async initializeFromRoute() {
      let payload = this.getEditingMaterialityPayload;
      // If Vuex store is empty (page reload), try to restore from sessionStorage
      if (!payload) {
        payload = this.restoreFromSessionStorage();
      }

      if (payload) {
        // Save to sessionStorage for page reload persistence
        this.saveToSessionStorage(payload);

        const { item, cycleType, editType, ruId, selected, isViewMode } = payload;

        this.cycle = cycleType || 'Actual';
        this.editType = editType || 'single';
        this.isViewMode = !!isViewMode;
        this.selectedRUId = ruId || selected;

        // Set edit permissions
        if (this.isViewMode) {
          this.ruLevelEdit = { local: false, regional: false, enable: false };
        } else {
          this.ruLevelEdit = { local: true, regional: true, enable: true };
        }

        if (editType === 'bulk') {
          this.bulksubmit = true;
          this.infoMessage = 'Multiple materiality thresholds set at GL level';
          this.ruData = { __type: 'bulk', _id: selected };
        } else {
          this.bulksubmit = false;
          this.infoMessage = 'Multiple materiality thresholds set at GL level';
          // Deep copy item with null check
          if (item && Object.keys(item).length > 0) {
            this.ruData = this.$nova.deepCopyJson(item);
          } else {
            this.ruData = {};
          }
          this.ruData.__type = 'single';

          // Percentage conversion
          if (this.ruData.control_thresholds_percent) {
            this.ruData.control_thresholds_percent = this.ruData.control_thresholds_percent * 100;
          }
          if (this.ruData.management_thresholds_percent) {
            this.ruData.management_thresholds_percent = this.ruData.management_thresholds_percent * 100;
          }
        }

        // Initialize defaults
        this.initializeThresholdDefaults();

        // Load data from Vuex store (set by Threshold.vue)
        await this.loadDataFromStore();

        // Load additional data from API if needed (view mode)
        if (this.selectedRUId && !this.bulksubmit && this.isViewMode) {
          await this.getRUData();
        }
      } else {
        // No payload available from Vuex or sessionStorage
        // Check route params as last resort
        const { cycleType, editType, isViewMode, selected } = this.$route.query;
        const ruId = this.$route.params.id || this.$route.query.ruId;

        // If no ruId or selected and no payload, redirect back to threshold list
        if (!ruId && !selected) {
          console.warn('[AddMateriality]: No valid data found, redirecting to threshold list');
          this.$store.commit('sbError', 'Materiality data not found. Please select a reporting unit again.', { root: true });
          this.$router.replace({ path: '/varianceanalysis/threshold' });
          return;
        }

        // Set cycle type
        this.cycle = cycleType || 'Actual';

        // Set edit type and bulk mode
        this.editType = editType || 'single';
        this.bulksubmit = editType === 'bulk';

        // Set view mode
        this.isViewMode = isViewMode === 'true';

        // Set edit permissions based on view mode
        if (this.isViewMode) {
          this.ruLevelEdit = { local: false, regional: false, enable: false };
        } else {
          this.ruLevelEdit = { local: true, regional: true, enable: true };
        }

        // Handle bulk vs single edit
        if (editType === 'bulk' && selected) {
          try {
            this.selectedRUId = JSON.parse(selected);
            this.ruData = { __type: 'bulk', _id: this.selectedRUId };
          } catch (e) {
            console.error('[AddMateriality]: Error parsing selected RUs', e);
            this.ruData = { __type: 'bulk' };
          }
        } else {
          this.selectedRUId = ruId;
          this.ruData = { __type: 'single' };

          // Retrieve item data from sessionStorage (legacy support)
          const storedItem = sessionStorage.getItem('materialityEditItem');
          if (storedItem) {
            try {
              const parsedItem = JSON.parse(storedItem);
              this.ruData = { ...this.ruData, ...parsedItem };
              sessionStorage.removeItem('materialityEditItem');
            } catch (e) {
              console.error('[AddMateriality]: Error parsing stored item', e);
            }
          }
        }

        // Initialize default threshold values
        this.initializeThresholdDefaults();

        // Try to load data from store first
        await this.loadDataFromStore();

        // Load additional data if needed
        if (this.selectedRUId && !this.bulksubmit && this.isViewMode) {
          await this.getRUData();
        }
      }
    },
    /**
     * Initialize default threshold values for the ruData
     */
    initializeThresholdDefaults() {
      this.ruData = {
        ...this.ruData,
        __type: this.editType,
        ...MATERIALITY.getDefaultRUThresholds(),
      };

      // Apply percentage conversion for single edit
      if (this.editType === 'single' && this.ruData.control_thresholds_percent) {
        this.ruData.control_thresholds_percent = this.ruData.control_thresholds_percent * 100;
      }
      if (this.editType === 'single' && this.ruData.management_thresholds_percent) {
        this.ruData.management_thresholds_percent = this.ruData.management_thresholds_percent * 100;
      }
    },
    /**
     * Generate formatted GL list for display
     * Similar to generateFormattedGLList in Threshold.vue
     * @param {Object} dt - GL data keyed by GL code
     * @param {boolean} ismulti - Whether multiple periods are selected
     * @returns {Array} - Formatted GL array for display
     */
    generateFormattedGLList(dt, ismulti = false) {
      const ArrayTemp = [];
      this.addeditGLData = {};
      this.glError = {};

      (this.glList.primary || []).forEach((i) => {
        this.addeditGLData[i._id] = this.getFormattedGLData(dt[i._id], ismulti);
        this.glError[i._id] = {};
        ArrayTemp.push(i);

        // Process child GLs
        (this.glList[i.code] || []).forEach((ii) => {
          ii.__child = true;
          this.addeditGLData[ii._id] = this.getFormattedGLData(dt[ii._id], ismulti);
          this.glError[ii._id] = {};
          ArrayTemp.push(ii);
        });
      });

      // Update glData reference
      this.glData = { ...this.addeditGLData };
      return ArrayTemp;
    },
    /**
     * Get formatted GL data for a single GL item
     * Similar to getFormatedGLData in Threshold.vue
     * @param {Object} i - GL item data
     * @param {boolean} ismulti - Whether multiple periods are selected
     * @returns {Object} - Formatted threshold data
     */
    getFormattedGLData(i = {}, ismulti = false) {
      const thresholdKeys = MATERIALITY.THRESHOLD_KEYS;
      const data = {};

      if (ismulti) {
        thresholdKeys.forEach(key => {
          data[key] = MATERIALITY.DEFAULT_THRESHOLD_VALUE;
        });
      } else {
        thresholdKeys.forEach(key => {
          if (this.isViewMode) {
            data[key] = i[key] || 0;
          } else {
            data[key] = MATERIALITY.DEFAULT_THRESHOLD_VALUE;
          }
        });
      }

      // Set default control percentages
      data.control_thresholds_percent_proforma = MATERIALITY.DEFAULT_CONTROL_PERCENT;
      data.control_thresholds_percent_regional_proforma = MATERIALITY.DEFAULT_CONTROL_PERCENT;

      return data;
    },
    /**
     * Fetch RU data from Vuex store action for view mode
     * Uses fetchMyMateriality action from varianceanalysis/reportingUnit store
     * @param {Object|string} payload - Either a payload object with period and reporting_type, or a period string
     */
    async getRUData(payload) {
      this.loading = true;
      
      // Clear materiality view periods when payload is undefined
      if (payload === undefined) {
        this.SET_MATERIALITY_VIEW_PERIODS([]);
      }
      
      // Handle both object payload and string period for backward compatibility
      let selectedperiod;
      let reportingType;
      let cycleType;
      
      if (typeof payload === 'object' && payload !== null) {
        selectedperiod = payload.period;
        reportingType = payload.reporting_type;
        cycleType = payload.cycletype;
      } else {
        // payload is a string (period) or undefined - don't access properties
        selectedperiod = payload;
        reportingType = this.ruData.reporting_type || this.internalReportingType;
        cycleType = this.cycle;
      }

      console.log(payload,"payload");
      

      // Default to current month/year if period not provided
      if (!selectedperiod) {
        const date = new Date();
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        selectedperiod = `${year}-${month}`;
      }

      // Update internalReportingType from cycle
      this.internalReportingType = reportingType || this.internalReportingType;

      try {
        // Use Vuex store action instead of direct axios call
        const result = await this.fetchMyMateriality({
          reportingUnitId: this.selectedRUId,
          cycletype: cycleType || this.cycle,
          period: selectedperiod,
          reporting_type: this.internalReportingType,
          client: this.$store.state.client,
          axiosInstance: this.axios
        });

        if (result && result.hasData) {
          // Get GL data map from store result
          const ndt = result.glDataMap || {};

          // Update local view data from store (already committed by action)
          this.viewRULevelMaterialityData = result.ruLevelMateriality || {};
          this.viewGLLevelMaterialityData = result.glLevelMateriality || {};
          this.viewSLLevelMaterialityData = result.slLevelMateriality || {};

          // Process RU level threshold data
          const thresholdKeys = MATERIALITY.THRESHOLD_KEYS;
          const absoluteValues = result.absoluteThresholdValues || {};

          thresholdKeys.forEach(key => {
            if (typeof absoluteValues[key] === 'undefined' || absoluteValues[key] === null) {
              this.ruData[key] = 0.001;
            } else {
              this.ruData[key] = absoluteValues[key] || 0;
            }
          });

          // Set control percentages to default
          this.ruData.control_thresholds_percent_proforma = 999999;
          this.ruData.control_thresholds_percent_regional_proforma = 999999;

          // Generate formatted GL data
          this.formattedGLData = this.generateFormattedGLList(ndt, false);
        } else {
          // Reset when no data
          this.viewRULevelMaterialityData = {};
          this.viewGLLevelMaterialityData = {};
          this.viewSLLevelMaterialityData = {};
        }
      } catch (err) {
        console.error('[AddMateriality]: Error fetching RU data', err);
        // Error is already handled by the store action with sbError commit
      } finally {
        this.loading = false;
      }
    },
    /**
     * Navigate back to the threshold list page
     * Clears sessionStorage before navigating to prevent stale data
     */
    goBack() {
      this.clearSessionStorage();
      this.$router.back();
    },
    /**
     * Remove a month from selected periods
     * Also removes corresponding entries from formattedRUData and formattedRUErrorData
     * @param {string} month - Month to remove (in YYYY-MM format)
     */
    removeMonth(month) {
      console.log('[AddMateriality]: Removing month from periods', month);
      
      if (this.ruData.periods && Array.isArray(this.ruData.periods)) {
        const index = this.ruData.periods.indexOf(month);
        if (index > -1) {
          this.ruData.periods.splice(index, 1);
          
          // Convert period format from YYYY-MM to MM-YYYY (key format used in formattedRUData)
          const periodKey = month.split('-').reverse().join('-');
          
          // Remove from formattedRUData using Vue's $delete for reactivity
          if (this.formattedRUData && this.formattedRUData[periodKey]) {
            this.$delete(this.formattedRUData, periodKey);
          }
          
          // Remove from formattedRUErrorData using Vue's $delete for reactivity
          if (this.formattedRUErrorData && this.formattedRUErrorData[periodKey]) {
            this.$delete(this.formattedRUErrorData, periodKey);
          }
          
          console.log('[AddMateriality]: Removed period', periodKey, 'Remaining periods:', this.ruData.periods);
        }
      }
    },
    /**
     * Handle GL edit event
     * @param {Object} event - Event with type and id
     */
    handleEditGL(event) {
      if (event && event.type && event.id) {
        this.editableGLs[event.type].push(event.id);

        // Initialize GL data structure if not exists
        if (!this.glData[event.id]) {
          this.$set(this.glData, event.id, MATERIALITY.getDefaultGLThresholds());
        }

        // Also initialize in addeditGLData
        if (!this.addeditGLData[event.id]) {
          this.$set(this.addeditGLData, event.id, MATERIALITY.getDefaultGLThresholds());
        }
      }
    },
    /**
     * Handle cancel GL edit event
     * @param {Object} event - Event with type and id
     */
    handleCancelEditGL(event) {
      if (event && event.type && event.id) {
        const index = this.editableGLs[event.type].indexOf(event.id);
        if (index > -1) {
          this.editableGLs[event.type].splice(index, 1);
        }
        // Reset the values for this GL ID
        if (this.glData[event.id]) {
          this.$set(this.glData, event.id, MATERIALITY.getDefaultGLThresholds());
        }
      }
    },
    /**
     * Handle SL edit event
     * @param {Object} event - Event with type and id
     */
    handleEditSL(event) {
      if (event && event.type && event.id) {
        this.editableSLs[event.type].push(event.id);

        // Initialize SL data structure if not exists
        if (!this.slData[event.id]) {
          this.$set(this.slData, event.id, MATERIALITY.getDefaultSLThresholds());
        }

        // Also initialize in addeditSLData (kept for backwards compatibility)
        if (!this.addeditSLData[event.id]) {
          this.$set(this.addeditSLData, event.id, MATERIALITY.getDefaultSLThresholds());
        }
      }
    },
    /**
     * Handle cancel SL edit event
     * @param {Object} event - Event with type and id
     */
    handleCancelEditSL(event) {
      if (event && event.type && event.id) {
        const index = this.editableSLs[event.type].indexOf(event.id);
        if (index > -1) {
          this.editableSLs[event.type].splice(index, 1);
        }
        // Reset the values for this SL ID
        if (this.slData[event.id]) {
          this.$set(this.slData, event.id, MATERIALITY.getDefaultSLThresholds());
        }
      }
    },
    /**
     * Handle RU edit event
     * Adds the period key to the editable list and enables editing
     * @param {Object} event - Event with type (proforma/forecast) and id (periodKey)
     */
    handleEditRu(event) {
      if (event && event.type && event.id) {
        // Add to editable list if not already present
        if (this.editableRUs[event.type].indexOf(event.id) === -1) {
          this.editableRUs[event.type].push(event.id);
        }
      }
    },
    /**
     * Handle cancel RU edit event
     * Removes the period key from the editable list
     * @param {Object} event - Event with type (proforma/forecast) and id (periodKey)
     */
    handleCancelEditRu(event) {
      if (event && event.type && event.id) {
        const index = this.editableRUs[event.type].indexOf(event.id);
        if (index > -1) {
          this.editableRUs[event.type].splice(index, 1);
        }
      }
    },
    /**
     * Update RU level threshold
     * Handles both single and multiple materiality updates
     */
    /**
     * Reset ruData threshold values to empty after successful update
     * Preserves metadata like _id, code, name, etc.
     */
    resetRuDataThresholds() {
      const thresholdKeys = [
        'control_thresholds_amount_proforma', 'control_thresholds_percent_proforma',
        'management_thresholds_amount_proforma', 'management_thresholds_percent_proforma',
        'control_thresholds_amount_regional_proforma', 'control_thresholds_percent_regional_proforma',
        'management_thresholds_amount_regional_proforma', 'management_thresholds_percent_regional_proforma',
        'control_thresholds_amount_forecast', 'control_thresholds_percent_forecast',
        'management_thresholds_amount_forecast', 'management_thresholds_percent_forecast',
        'control_thresholds_amount_regional_forecast', 'control_thresholds_percent_regional_forecast',
        'management_thresholds_amount_regional_forecast', 'management_thresholds_percent_regional_forecast'
      ];

      // Reset threshold values to empty string
      thresholdKeys.forEach(key => {
        this.$set(this.ruData, key, '');
      });

      // Reset periods array
      this.$set(this.ruData, 'periods', []);

      // Reset formattedRUData for multiple materiality mode
      this.formattedRUData = {};
      this.formattedRUErrorData = {};

      console.log('[AddMateriality]: RU threshold values reset');
    },
    /**
     * Reset GL data threshold values
     */
    resetGLDataThresholds() {
      this.editableGLs = { proforma: [], forecast: [] };
      this.generateFormattedGLList({}, true);
      console.log('[AddMateriality]: GL threshold values reset');
    },
    /**
     * Reset SL data threshold values
     */
    resetSLDataThresholds() {
      this.editableSLs = { proforma: [], forecast: [] };
      this.slData = {};
      this.addeditSLData = {};
      console.log('[AddMateriality]: SL threshold values reset');
    },
    /**
     * Update RU level threshold - Wrapper method
     * Sets flags and calls central updateThreshold method
     */
    updateRUThreshold() {
      this.singleupdatetype = false;
      this.materialitySLLevelType = '';
      this.updateThreshold();
    },
    /**
     * Update GL level threshold - Wrapper method
     * Sets flags and calls central updateThreshold method
     */
    updateGLThreshold() {
      this.singleupdatetype = true;
      this.materialitySLLevelType = '';
      this.updateThreshold();
    },
    /**
     * Update SL level threshold - Wrapper method
     * Sets flags and calls central updateThreshold method
     */
    updateSLThreshold() {
      this.singleupdatetype = false;
      this.materialitySLLevelType = 'materialitysllevel';
      this.updateThreshold();
    },
    /**
     * Central threshold update method
     * Handles RU, GL, and SL level updates based on flags
     */
    async updateThreshold() {
      this.loading = true;

      // Format periods from YYYY-MM to MM-YYYY
      const formattedPeriods = this.ruData.periods && Array.isArray(this.ruData.periods)
        ? this.formatPeriod(this.ruData.periods)
        : [];

      let newdata = {
        cycle: this.cycle,
        periods: formattedPeriods,
        reportingunit: this.selectedRUId,
        multiplematerialities: true,
        reporting_type: this.ruData.reporting_type,
      };

      // RU Level Update
      if (!this.singleupdatetype && !this.materialitySLLevelType) {
        newdata.type = "materialityrulevel";
        if (this.bulksubmit) {
          newdata.bulk = true;
          newdata.type = "bulkmaterialityrulevel";
        }

        // Multiple Materiality Mode
        if (this.multipleMaterialityToggle) {
          // Sanitize formattedRUData to ensure control percent keys have proper defaults
          const controlPercentKeys = ['control_thresholds_percent_proforma', 'control_thresholds_percent_regional_proforma'];
          const sanitizedMateriality = {};
          Object.keys(this.formattedRUData).forEach(period => {
            sanitizedMateriality[period] = { ...this.formattedRUData[period] };
            controlPercentKeys.forEach(key => {
              const value = sanitizedMateriality[period][key];
              if (value === '' || value === null || value === undefined) {
                sanitizedMateriality[period][key] = MATERIALITY.DEFAULT_CONTROL_PERCENT;
              }
            });
          });
          newdata.materiality = sanitizedMateriality;
          newdata.multiplematerialities = true;
        }
        // Single Materiality Mode
        else {
          newdata.materiality = {};
          let data = {};
          const thresholdKeys = [
            'control_thresholds_amount_proforma', 'control_thresholds_percent_proforma',
            'management_thresholds_amount_proforma', 'management_thresholds_percent_proforma',
            'control_thresholds_amount_regional_proforma', 'control_thresholds_percent_regional_proforma',
            'management_thresholds_amount_regional_proforma', 'management_thresholds_percent_regional_proforma',
            'control_thresholds_amount_forecast', 'control_thresholds_percent_forecast',
            'management_thresholds_amount_forecast', 'management_thresholds_percent_forecast',
            'control_thresholds_amount_regional_forecast', 'control_thresholds_percent_regional_forecast',
            'management_thresholds_amount_regional_forecast', 'management_thresholds_percent_regional_forecast'
          ];

          // Control percent keys need different default value (999999)
          const controlPercentKeys = ['control_thresholds_percent_proforma', 'control_thresholds_percent_regional_proforma'];
          thresholdKeys.forEach(key => {
            const value = this.ruData[key];
            // Handle null, undefined, and empty strings
            if (value === null || value === undefined || value === '') {
              newdata.materiality[key] = controlPercentKeys.includes(key) 
                ? MATERIALITY.DEFAULT_CONTROL_PERCENT 
                : MATERIALITY.DEFAULT_THRESHOLD_VALUE;
            } else {
              newdata.materiality[key] = value;
            }
          });

          // Remove placeholder values (0.001 for amounts, 999999 for control percentages)
          thresholdKeys.forEach(key => {
            if (newdata.materiality[key] === MATERIALITY.DEFAULT_THRESHOLD_VALUE) {
              delete newdata.materiality[key];
            }
          });

          if (this.ruData.periods && Array.isArray(this.ruData.periods)) {
            this.ruData.periods.forEach((period) => {
              const formattedPeriod = this.formatPeriod(period);
              data = {
                ...data,
                [formattedPeriod]: newdata.materiality
              };
            });
          }
          newdata.materiality = data;
          newdata.multiplematerialities = false;
        }

        try {
          await this.saveMaterialityThreshold({
            payload: newdata,
            axiosInstance: this.axios
          });
          // Reset threshold values after successful update
          this.resetRuDataThresholds();
          // this.goBack();
        } catch (error) {
          console.error('[AddMateriality]: Error updating RU threshold', error);
        } finally {
          this.loading = false;
        }
      }
      // GL Level Update
      else if (this.singleupdatetype && !this.materialitySLLevelType) {
        newdata.type = "materialitygllevel";
        if (this.bulksubmit) {
          newdata.bulk = true;
          newdata.type = "bulkmaterialitygllevel";
        }

        let ndt = [];
        let uniqueeditablegls = Array.from(new Set(this.editableGLs.proforma.concat(this.editableGLs.forecast)));

        for (const i of uniqueeditablegls) {
          Object.keys(this.addeditGLData[i]).forEach(key => {
            if (this.addeditGLData[i][key] === 0.001) {
              delete this.addeditGLData[i][key];
            }
          });
          let td = { ...(this.addeditGLData[i] || {}) };
          td.glcode = i;
          ndt.push(td);
        }
        newdata.materiality = ndt;

        try {
          await this.saveMaterialityThreshold({
            payload: newdata,
            axiosInstance: this.axios
          });
          this.resetGLDataThresholds();
          // this.goBack();
        } catch (error) {
          console.error('[AddMateriality]: Error updating GL threshold', error);
        } finally {
          this.loading = false;
        }
      }
      // SL Level Update
      else if (this.materialitySLLevelType === 'materialitysllevel') {
        newdata.type = "materialitysllevel";
        if (this.bulksubmit) {
          newdata.bulk = true;
          newdata.type = "bulkmaterialitysllevel";
        }

        let ndt = [];
        let uniqueeditablesls = Array.from(new Set(this.editableSLs.proforma.concat(this.editableSLs.forecast)));

        // Use slData directly since child component v-model binds to slData (passed as glData prop)
        for (const i of uniqueeditablesls) {
          if (!this.slData[i]) continue;

          // Create a copy of the data
          let td = { ...this.slData[i] };

          // Remove placeholder values (0.001)
          Object.keys(td).forEach(key => {
            if (td[key] === 0.001) {
              delete td[key];
            }
          });

          td.glcode = i;
          ndt.push(td);
        }
        newdata.materiality = ndt;

        try {
          await this.saveMaterialityThreshold({
            payload: newdata,
            axiosInstance: this.axios
          });
          this.resetSLDataThresholds();
          // this.goBack();
        } catch (error) {
          console.error('[AddMateriality]: Error updating SL threshold', error);
        } finally {
          this.loading = false;
        }
      }
    },
    /**
     * Save payload to sessionStorage for page reload persistence
     * @param {Object} payload - The materiality editing payload
     */
    saveToSessionStorage(payload) {
      try {
        if (payload) {
          sessionStorage.setItem('materiality_editing_payload', JSON.stringify(payload));
        }
      } catch (e) {
        console.error('[AddMateriality]: Error saving to sessionStorage', e);
      }
    },
    /**
     * Restore payload from sessionStorage after page reload
     * @returns {Object|null} - The restored payload or null
     */
    restoreFromSessionStorage() {
      try {
        const stored = sessionStorage.getItem('materiality_editing_payload');
        if (stored) {
          const payload = JSON.parse(stored);
          return payload;
        }
      } catch (e) {
        console.error('[AddMateriality]: Error restoring from sessionStorage', e);
      }
      return null;
    },
    /**
     * Update ruData in sessionStorage when it changes
     * @param {Object} ruData - The updated ruData
     */
    updateSessionStorageRuData(ruData) {
      try {
        const stored = sessionStorage.getItem('materiality_editing_payload');
        if (stored) {
          const payload = JSON.parse(stored);
          // Update item in payload with current ruData (excluding __type)
          // eslint-disable-next-line no-unused-vars
          const { __type, ...itemData } = ruData;
          payload.item = { ...payload.item, ...itemData };
          sessionStorage.setItem('materiality_editing_payload', JSON.stringify(payload));
        }
      } catch (e) {
        console.error('[AddMateriality]: Error updating sessionStorage', e);
      }
    },
    /**
     * Clear sessionStorage data
     * Called when navigating away or on explicit clear
     */
    clearSessionStorage() {
      try {
        sessionStorage.removeItem('materiality_editing_payload');
        sessionStorage.removeItem('materialityEditItem');
      } catch (e) {
        console.error('[AddMateriality]: Error clearing sessionStorage', e);
      }
    },
  },
  /**
   * Watchers for reactive updates
   */
  watch: {
    /**
     * Watch ruData changes and persist to sessionStorage
     * Ensures data survives page reloads
     */
    ruData: {
      handler(newVal) {
        if (newVal && Object.keys(newVal).length > 0) {
          this.updateSessionStorageRuData(newVal);
        }
      },
      deep: true
    },
    /**
     * Watch for changes in periods and regenerate formatted RU data
     * Only when multiple materiality toggle is enabled
     */
    'ruData.periods': {
      handler(newPeriods, oldPeriods) {
        if (this.multipleMaterialityToggle && newPeriods && newPeriods.length > 0) {
          // Check if periods actually changed
          const periodsChanged = JSON.stringify(newPeriods) !== JSON.stringify(oldPeriods);
          if (periodsChanged) {
            this.generateFormattedRUData();
          }
        }
      },
      deep: true
    },
    /**
     * Watch for reporting_type changes and refetch GL list + clear periods
     * Triggered when user changes the statement dropdown (pl/bs)
     * Only applies to EDIT mode - in view mode, the filters are handled separately
     */
    'ruData.reporting_type': {
      handler(newReportingType, oldReportingType) {
        // Skip edit-mode logic when in view mode
        // In view mode, reporting_type changes are handled via viewModuleType and getRUData
        if (this.isViewMode) {
          return;
        }
        if (newReportingType && newReportingType !== oldReportingType) {
          // Sync internalReportingType with ruData.reporting_type
          this.internalReportingType = newReportingType;
          // Refetch GL list for new reporting type
          this.fetchGLList(newReportingType);
          // Clear periods when reporting_type changes
          if (this.ruData.periods && this.ruData.periods.length > 0) {
            this.ruData.periods = [];
          }
          // Also clear formatted data
          this.formattedRUData = {};
          this.formattedRUErrorData = {};
        }
      },
      immediate: false
    },
    /**
     * Watch for multiple materiality toggle changes
     * Generate or clear formatted RU data accordingly
     */
    multipleMaterialityToggle(newValue) {
      if (newValue && this.ruData.periods && this.ruData.periods.length > 0) {
        // Toggle enabled - generate formatted data if we have periods
        this.generateFormattedRUData();
      } else if (!newValue) {
        // Toggle disabled - clear formatted data
        this.formattedRUData = {};
        this.formattedRUErrorData = {};
      }
    }
  },
  /**
   * Clean up on component destroy
   */
  beforeDestroy() {
    // Clear sessionStorage data
    this.clearSessionStorage();

    // Reset Vuex store materiality editing state
    this.$store.commit('varianceanalysis/reportingUnit/RESET_MATERIALITY_EDITING_STATE');

    // Reset component state
    this.ruData = {};
    this.glData = {};
    this.slData = {};
    this.glError = {};
    this.slError = {};
    this.glList = {};
    this.addeditGLData = {};
    this.addeditSLData = {};
    this.formattedGLData = [];
    this.formattedRUData = {};
    this.formattedRUErrorData = {};
    this.editableGLs = { proforma: [], forecast: [] };
    this.editableSLs = { proforma: [], forecast: [] };
    this.editableRUs = { proforma: [], forecast: [] };
    this.viewRULevelMaterialityData = [];
    this.viewGLLevelMaterialityData = [];
    this.viewSLLevelMaterialityData = [];
  },
};
</script>

<style lang="scss" scoped>
.wisetab {
  border-bottom: 1px solid #e5e5e5;
  border-radius: 0;
}

.wisetab .v-tab {
  padding: 4px 16px;
  min-width: auto;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
}
.wisetab .v-tab.v-tab--active {
  font-weight: 600;
}
.wisetab::v-deep .v-tabs-bar {
  height: 34px;
}

.border-top {
  border-top: 1px solid #e5e5e5;
}
</style>